import { column, Schema, Table } from '@powersync/common';

/**
 * The local SQLite schema PowerSync maintains on-device. It mirrors the synced
 * Postgres tables (see supabase/migrations + powersync/sync-config.yaml). Every
 * table has an implicit text `id`. SQLite has no boolean — `checked` is stored
 * as 0/1 and coerced at the Supabase upload boundary (see connector.ts).
 *
 * Keep this in lockstep with the migrations and the sync rules.
 */

const templates = new Table({
  owner_id: column.text,
  title: column.text,
  description: column.text,
  icon: column.text,
  deleted_at: column.text,
  created_at: column.text,
  updated_at: column.text,
});

const template_items = new Table(
  {
    template_id: column.text,
    position: column.integer,
    label: column.text,
    notes: column.text,
    created_at: column.text,
    updated_at: column.text,
  },
  { indexes: { by_template: ['template_id'] } },
);

const instances = new Table({
  template_id: column.text,
  owner_id: column.text,
  title: column.text,
  description: column.text,
  status: column.text,
  started_at: column.text,
  // When the run was sealed (stamped locally, kept by the server). The server owns
  // completed_recorded_at — never write it from the client.
  completed_at: column.text,
  completed_recorded_at: column.text,
  // Who sealed the run (active -> completed). Re-stamped server-side from the
  // caller; the display name resolves live from template_member_profiles.
  completed_by: column.text,
  created_at: column.text,
  updated_at: column.text,
});

const instance_items = new Table(
  {
    instance_id: column.text,
    source_template_item_id: column.text,
    position: column.integer,
    label: column.text,
    notes: column.text,
    checked: column.integer,
    // Who checked it; the display name is resolved live from
    // template_member_profiles (joined on template_id + checked_by).
    checked_by: column.text,
    // When the check was made (stamped locally, kept by the server). The server
    // owns checked_recorded_at — never write it from the client.
    checked_at: column.text,
    checked_recorded_at: column.text,
    // Denormalized owning template id — the sync routing key (see sync-config).
    template_id: column.text,
    created_at: column.text,
    updated_at: column.text,
  },
  { indexes: { by_instance: ['instance_id'] } },
);

// Append-only, immutable log notes on a run's steps (0–n per item). The author's
// display name is resolved live from template_member_profiles (joined on
// template_id + author_id), not snapshotted.
const instance_item_notes = new Table(
  {
    instance_id: column.text,
    instance_item_id: column.text,
    author_id: column.text,
    body: column.text,
    // When the note was written (stamped locally, kept by the server); the log
    // reads in this order. The server owns recorded_at — never write it here.
    created_at: column.text,
    recorded_at: column.text,
    // Denormalized owning template id — the sync routing key (see sync-config).
    template_id: column.text,
  },
  { indexes: { by_item: ['instance_item_id'], by_instance: ['instance_id'] } },
);

// The user's own profile (only), the source of their attribution name.
const profiles = new Table({
  display_name: column.text,
  created_at: column.text,
  updated_at: column.text,
});

// Membership of a template's invite links, with each collaborator's role. Synced
// to everyone with access so a device knows which templates it can reach and at
// what level; runs sync off template access, not this table directly.
const template_collaborators = new Table(
  {
    template_id: column.text,
    user_id: column.text,
    role: column.text,
    joined_at: column.text,
  },
  { indexes: { by_template: ['template_id'], by_user: ['user_id'] } },
);

// A template's members' display names, keyed by (template_id, user_id). The live
// source of run attribution: the client resolves instance_items.checked_by and
// instance_item_notes.author_id to a name by joining here on template_id + id.
// Maintained + synced server-side (see the 20260703000000 migration).
const template_member_profiles = new Table(
  {
    template_id: column.text,
    user_id: column.text,
    display_name: column.text,
  },
  { indexes: { by_template: ['template_id'] } },
);

export const AppSchema = new Schema({
  templates,
  template_items,
  instances,
  instance_items,
  instance_item_notes,
  template_collaborators,
  template_member_profiles,
  profiles,
});
